/**
  * @fileoverview Client-side onboarding wizard state and defaults for system-web.
  * @module system-web/pages/tenants/wizard/types
  *
  * Responsibilities:
  * - Define the local-first onboarding draft shape used by the wizard UI.
  * - Provide deterministic default values for new onboarding drafts.
  *
  * Dependencies:
  * - geojson types for selected NERIS boundary geometry.
  *
  * Architecture:
  * - Local-first. This state is written to Dexie first and later posted to the
  *   server only for the inherently-online provisioning call.
  *
  * Notes:
  * - `initialAdmin.sendInviteImmediately` controls whether provisioning sends
  *   the initial admin email immediately or stores it for a later tailored send.
  */
import type { Polygon, MultiPolygon } from 'geojson';
import type { BrandingLogoRefLike } from '@firesquirrel/core-domain';

export type WizardMode = 'admin' | 'self-service';

// ---------------------------------------------------------------------------
// Wizard State — mirrors the backend WizardState but with UI-friendly types
// ---------------------------------------------------------------------------

export interface LookupRef {
  lookupId?: string;
  lookupType: string;
  code: string;
  name: string;
}

export interface NerisVerification {
  status: 'unverified' | 'verified' | 'not_found' | 'skipped';
  verifiedAt?: string;
  nerisData?: {
    departmentName?: string;
    state?: string;
    county?: string;
    city?: string;
    fdid?: string;
    zipCode?: string;
    address?: string;
  };
}

export interface SelectedBoundary {
  nerisId: string;
  departmentName: string;
  departmentType: 'CAREER' | 'VOLUNTEER' | 'COMBINATION';
  geometry: Polygon | MultiPolygon;
  centroid: [number, number];
  area: number; // square miles
  bbox: [number, number, number, number];
  
  // From Platform API (enhanced)
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  contact?: {
    email: string;
    website: string;
  };
  metadata?: {
    entityType: string;
    population: number;
    staffing: number;
    stations: number;
  };
  
  verified: boolean;
  verifiedAt: string;
  verificationSources: {
    arcgis: boolean;
    platform: boolean;
  };
}

export type WizardCodeSetVendorId = 'icc' | 'nfpa' | 'custom';

/**
 * @contract WizardSelectedBook
 *
 * Purpose:
 * - Represents a vendor-published code book the user marked for download
 *   during onboarding. After provisioning, one BackgroundJob is enqueued
 *   per selected book.
 *
 * Fields:
 * - bookId:       Vendor-stable book identifier (e.g. ICC `shortCode`).
 * - title:        Display title.
 * - edition:      Printing/edition label.
 * - chapterCount: Optional informational chapter count from the catalog.
 *
 * Rules:
 * - bookId is required; title falls back to bookId on the wizard side.
 *
 * Sync:
 * - Wizard-state only. Persisted into Dexie via the onboarding draft service.
 */
export interface WizardSelectedBook {
  bookId: string;
  title: string;
  edition: string;
  chapterCount?: number;
}

export interface WizardRegulatoryVendor {
  vendorId: WizardCodeSetVendorId;
  clientId?: string;
  clientSecret?: string;
  apiKey?: string;
  subscriptionId?: string;
  /** Books the user selected for download after provisioning. */
  selectedBooks?: WizardSelectedBook[];
}

export interface CodeSetSubscription {
  setCode: string;        // e.g. 'IFC', 'IBC', 'IRC'
  title: string;          // e.g. 'International Fire Code'
  edition: string;        // e.g. '2021', '2024'
  annualCost: number;
  selected: boolean;
}

export interface WizardContact {
  _id?: string;
  name: string;
  title?: string;
  role?: string;
  isPrimary: boolean;
  communicationConsent?: boolean;
  emails: Array<{ type: string; address: string; isPrimary: boolean }>;
  phones: Array<{ type: string; number: string; countryCode?: string; extension?: string; isPrimary: boolean }>;
  notes?: string;
}

/**
 * @contract WizardParcelImport
 *
 * Purpose:
 * - Onboarding Step 9 parcel data import configuration. Captures the admin's
 *   chosen import path (Regrid/Manual/Skip), the auto-matched Regrid cache file,
 *   token entitlement for the Regrid path, and spatial filtering options.
 *
 * Fields:
 * - path:             Selected import path — 'regrid' | 'manual' | 'skip'.
 * - regridFileId:     `geoCacheFiles._id` of the selected/auto-matched cache
 *                     file. This is the 5-digit county FIPS code, NOT a blob
 *                     URL — system-api resolves it to the authoritative
 *                     `blobUri` at enqueue time.
 * - regridFileName:   Display name of the matched Regrid cache file.
 * - manualFileName:   Name of the uploaded file for the Manual path.
 * - tokensPerYear:    Number of parcel data updates per year (Regrid only).
 * - filterMode:       Spatial filtering mode — 'all' | 'department' | 'fireHazard'.
 * - hazardLevels:     Selected fire hazard levels when filterMode is 'fireHazard'.
 * - createOwnerContacts: Opt-in policy for materializing one primary-owner
 *                     contact record per imported parcel. Defaults to off.
 *                     Read by the provisioning route and carried on the Regrid
 *                     ingestion job payload as `createOwnerContacts`.
 *
 * Rules:
 * - `tokensPerYear` is only meaningful when `path === 'regrid'` and must be >= 1.
 * - `regridFileId` is set when `path === 'regrid'` and a matching cache file exists.
 * - `hazardLevels` is only meaningful when `filterMode === 'fireHazard'`.
 * - `createOwnerContacts` is only meaningful when `path === 'regrid'` and is
 *   strictly opt-in: absent or `false` means no contacts are created. Only an
 *   explicit `true` enables materialization, matching the provisioning route
 *   (`=== true`), the worker payload contract, and the post-onboarding Parcel
 *   Data tab. Never infer an opt-in from an absent value — a full-county import
 *   creates one PII-bearing contact per parcel.
 *
 * Sync:
 * - Wizard-state only. Persisted into Dexie via the onboarding draft service and
 *   sent to the provisioning API as part of `wizardState.initialDataSetup.parcelImport`.
 */
export interface WizardParcelImport {
  path: 'regrid' | 'manual' | 'skip';
  regridFileId?: string;
  regridFileName?: string;
  manualFileName?: string;
  tokensPerYear?: number;
  filterMode?: 'all' | 'department' | 'fireHazard';
  hazardLevels?: ('low' | 'medium' | 'high' | 'extreme')[];
  createOwnerContacts?: boolean;
}

export interface WizardState {
  currentStep: number;
  completedSteps: number[];

  // Step 1 — Department Info (includes jurisdiction, merged from old Step 5)
  legacySystem: {
    isExistingClient: boolean;
    database?: string;
    departmentId?: number;
    departmentData?: Record<string, any>;
  };
  departmentInfo: {
    name?: string;
    slug?: string;
    nerisId?: string;
    nerisVerification?: NerisVerification;
    selectedBoundary?: SelectedBoundary;
    /**
     * NERIS pygeoapi feature URL for this department's boundary, captured from
     * the verified entity's `region_sets` at lookup time. Persisted so the
     * boundary can be re-fetched later (retry button / returning to Step 1)
     * without redoing the entity search — the polygon endpoint returns null
     * geometry when no region URL is supplied.
     */
    nerisBoundaryUrl?: string;
    address?: { street?: string; city?: string; state?: string; zip?: string; county?: string; country?: string };
    contactEmail?: string;
    contactPhone?: string;
    timezone?: string;
    departmentType?: string;
    departmentClassification?: string;
    // Jurisdiction (merged — previously Step 5)
    jurisdiction?: {
      country?: LookupRef;
      state?: LookupRef;
      county?: LookupRef;
      fipsCode?: string;
      city?: string;
    };
  };
  branding: {
    /** Legacy plain-URL logo. Retained as a fallback when `logo` is absent. */
    logoUrl?: string;
    /** Canonical uploaded department logo (primary / left slot). */
    logo?: BrandingLogoRefLike;
    /** Canonical uploaded department logo (secondary / right slot). */
    secondLogo?: BrandingLogoRefLike;
    loginBackgroundUrl?: string;
    backgroundOpacity?: number;
    primaryColor?: string;
    secondaryColor?: string;
  };

  // Step 2
  organizationId: string | null;
  tier: string;

  // Step 3
  billing: {
    billingEntity?: string;
    subscriptionType?: string;
    startDate?: string;
    billingCycle?: string;
    contractedAmount?: number;
    billingContact?: { name?: string; email?: string; phone?: string };
    fiscalYear?: { startMonth?: number; startDay?: number };
  };
  trial?: {
    durationDays?: number;
    supportLevel?: string;
    departmentContact?: { name?: string; email?: string; phone?: string; title?: string };
    salesNotes?: string;
  };

  // Step 4 — Inspection Plan
  inspectionPlan: {
    estimatedInitialPerYear?: number;
    estimatedReInspectionsPerYear?: number;
    volumeBand?: string;
    maxSeriesPerYear?: number;
    commitmentTerm?: string;
    commitmentDiscount?: number;
    annualPrice?: number;
    overagePolicy?: string;
    overageRate?: number;
    source?: string;
  };

  // Step 5 — Contacts
  contacts: WizardContact[];

  // Step 5 — Regulatory Framework (new)
  regulatory: {
    primaryFireCode?: 'IFC' | 'NFPA' | 'state_specific' | 'custom';
    codeEdition?: string;
    jurisdictionVariant?: string;
    vendors: WizardRegulatoryVendor[];
  };

  /**
   * File names of pending fee schedule uploads (display only).
   * Actual File objects are held in component state and are not serializable.
   * Documents will be uploaded after the department is provisioned.
   */
  pendingFeeScheduleFileNames?: string[];

  /**
   * File names of pending ordinance uploads (display only).
   * Actual File objects are held in component state and are not serializable.
   */
  pendingOrdinanceFileNames?: string[];

  // Step 6 — Modules & Settings
  modules: Array<{ code: string; isEnabled: boolean }>;
  codeSetSubscriptions: CodeSetSubscription[];
  dataRetentionPolicy: {
    inspectionRetentionYears?: number;
    photoRetentionYears?: number;
    documentRetentionYears?: number;
    reportRetentionYears?: number;
    auditLogRetentionYears?: number;
    deletionPolicy?: 'archive' | 'purge';
    policyNotes?: string;
  };
  notificationPreferences: {
    overdueInspections?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    upcomingInspections?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    violationsFound?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    reinspectionDue?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    inspectionCompleted?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    newUserInvite?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    storageThreshold?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    volumeThreshold?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
    systemMaintenance?: { enabled: boolean; channelEmail: boolean; channelSms: boolean; channelPush: boolean };
  };
  mapDefaults: {
    defaultZoom?: number;
    mapStyle?: 'streets' | 'satellite' | 'hybrid';
    defaultCenter?: { lat: number; lng: number };
    trafficLayer?: boolean;
    measurementUnit?: 'imperial' | 'metric';
    clusterMarkers?: boolean;
  };

  // Step 7 — Admin & Authentication
  authenticationSettings: {
    authMethod?: 'local' | 'microsoft365' | 'google' | 'saml';
    mfa?: {
      required: boolean;
      methods: Array<'sms' | 'email' | 'totp' | 'fido2'>;
    };
    microsoft365?: {
      tenantId?: string;
      domain?: string;
      consentGranted?: boolean;
    };
    google?: {
      workspaceDomain?: string;
      consentGranted?: boolean;
    };
    saml?: {
      idpEntityId?: string;
      idpSsoUrl?: string;
      idpCertificate?: string;
      metadataUrl?: string;
    };
    sessionPolicy?: {
      adminIdleTimeoutMinutes?: number;
      mobileTokenRefreshHours?: number;
    };
  };
  initialAdmin: {
    selectionMode?: 'existing_user' | 'contact' | 'manual';
    existingUserId?: string;  // For existing system user
    contactId?: string;        // For contact from Step 3
    firstName?: string;
    lastName?: string;
    email?: string;
    sendInviteImmediately?: boolean;
  };

  // Step 8 — Initial Data Setup
  initialDataSetup: {
    inspectionAccounts: Array<{
      name: string;
      accountType: string;
      isDefault: boolean;
    }>;
    seedViolationCodes: boolean;
    seedNotificationTemplates: boolean;
    includeSampleData: boolean;
    /** Parcel data import configuration (Phase 9). Defaults to skip. */
    parcelImport?: WizardParcelImport;
  };

  // Step 9 — Review & Create
  tosAcknowledged?: boolean;

  // Wizard mode — controls field visibility and required rules
  wizardMode?: WizardMode;
}

// 10 steps — Jurisdiction merged into Step 1, Contacts moved to Step 3, Regulatory added at Step 6
export const STEP_LABELS = [
  'Department Info',           // Step 1
  'Organization & Tier',       // Step 2
  'Contacts',                  // Step 3
  'Billing',                   // Step 4
  'Inspection Plan',           // Step 5
  'Regulatory Framework',      // Step 6 (new)
  'Modules & Settings',        // Step 7
  'Admin & Auth',              // Step 8
  'Initial Data Setup',        // Step 9
  'Review & Create',           // Step 10
];

export function emptyWizardState(): WizardState {
  return {
    currentStep: 1,
    completedSteps: [],
    legacySystem: { isExistingClient: false },
    departmentInfo: { address: { country: 'US' } },
    branding: { backgroundOpacity: 0.15, primaryColor: '#B22222', secondaryColor: '#1C1C1C' },
    organizationId: null,
    tier: 'professional',
    billing: {
      billingEntity: 'department',
      subscriptionType: 'active',
      billingCycle: 'annual',
      contractedAmount: 0,
      billingContact: {},
      fiscalYear: { startMonth: 1, startDay: 1 },
    },
    inspectionPlan: {
      estimatedInitialPerYear: 0,
      estimatedReInspectionsPerYear: 0,
      volumeBand: 'small',
      maxSeriesPerYear: 500,
      commitmentTerm: 'annual',
      commitmentDiscount: 0,
      annualPrice: 0,
      overagePolicy: 'block',
      source: 'department',
    },
    contacts: [],
    regulatory: {
      vendors: [],
    },
    modules: [],
    codeSetSubscriptions: [],
    dataRetentionPolicy: {
      inspectionRetentionYears: 7,
      photoRetentionYears: 7,
      documentRetentionYears: 10,
      reportRetentionYears: 10,
      auditLogRetentionYears: 7,
      deletionPolicy: 'archive',
    },
    notificationPreferences: {},
    mapDefaults: { defaultZoom: 12, mapStyle: 'streets', trafficLayer: false, measurementUnit: 'imperial', clusterMarkers: true },
    authenticationSettings: {
      authMethod: 'local',
      sessionPolicy: { adminIdleTimeoutMinutes: 60, mobileTokenRefreshHours: 24 },
    },
    initialAdmin: { firstName: '', lastName: '', email: '', sendInviteImmediately: true },
    initialDataSetup: {
      inspectionAccounts: [
        { name: 'General Inspection Account', accountType: 'general', isDefault: true },
      ],
      seedViolationCodes: true,
      seedNotificationTemplates: true,
      includeSampleData: true,
      parcelImport: { path: 'skip' },
    },
    tosAcknowledged: false,
  };
}
